import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function sameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function between(date, a, b) {
  if (!a || !b || !date) return false;
  const d = date.getTime(),
    lo = Math.min(a.getTime(), b.getTime()),
    hi = Math.max(a.getTime(), b.getTime());
  return d > lo && d < hi;
}

function MonthGrid({ year, month, start, end, hovered, onDayClick, onDayHover, minDate, maxDate }) {
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // The effective end for preview purposes
  const previewEnd = end || hovered;
  const rangeFrom = start && previewEnd ? (start <= previewEnd ? start : previewEnd) : start;
  const rangeTo = start && previewEnd ? (start <= previewEnd ? previewEnd : start) : null;

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div style={{ width: "100%" }}>
      <div style={{ textAlign: "center", fontWeight: 600, color: "#1e293b", marginBottom: 12, fontSize: 14 }}>
        {MONTHS[month]} {year}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 4 }}>
        {DAYS.map(d => (
          <div
            key={d}
            style={{ textAlign: "center", fontSize: 11, color: "#94a3b8", fontWeight: 600, padding: "4px 0" }}
          >
            {d}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {cells.map((date, i) => {
          if (!date) return <div key={i} style={{ height: 36 }} />;

          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const disabled = (minDate && date < minDate) || (maxDate && date > maxDate);
          const isStart = rangeFrom && sameDay(date, rangeFrom);
          const isEnd = rangeTo && sameDay(date, rangeTo);
          const isIn = rangeFrom && rangeTo && between(date, rangeFrom, rangeTo);
          const isToday = sameDay(date, today);
          const isFinalEnd = end && sameDay(date, end);

          // Background strip for in-range days
          let wrapStyle = {
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            backgroundColor: "transparent",
          };

          if (!disabled) {
            if (isIn) wrapStyle.backgroundColor = "#dbeafe"; // blue-100
            if (isStart && rangeTo) wrapStyle.borderRadius = "50% 0 0 50%";
            if (isEnd && rangeFrom) wrapStyle.borderRadius = "0 50% 50% 0";
            if (isStart && sameDay(date, rangeTo)) wrapStyle.borderRadius = "50%"; // single day
          }

          // The circle button on top
          let circleStyle = {
            width: 32,
            height: 32,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            cursor: disabled ? "not-allowed" : "pointer",
            fontWeight: isStart || isEnd || isFinalEnd ? 700 : 400,
            position: "relative",
            zIndex: 1,
            userSelect: "none",
            transition: "background 0.1s",
          };

          if (disabled) {
            circleStyle.color = "#cbd5e1";
          } else if (isStart || isEnd) {
            circleStyle.backgroundColor = "#2563eb"; // blue-600
            circleStyle.color = "#fff";
          } else if (isIn) {
            circleStyle.color = "#1e40af";
          } else if (isToday) {
            circleStyle.backgroundColor = "#f1f5f9";
            circleStyle.color = "#1e293b";
            circleStyle.fontWeight = 600;
          } else {
            circleStyle.color = "#374151";
          }

          return (
            <div
              key={i}
              style={wrapStyle}
              onClick={() => !disabled && onDayClick(date)}
              onMouseEnter={() => !disabled && onDayHover(date)}
            >
              <div style={circleStyle}>{date.getDate()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SimpleRangePicker({ rangeStart, rangeEnd, onRangeChange, minDate, maxDate }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [hovered, setHovered] = useState(null);

  const m2 = new Date(viewYear, viewMonth + 1, 1);

  const handleClick = date => {
    if (!rangeStart || rangeEnd) {
      onRangeChange({ start: date, end: null });
      setHovered(null);
    } else {
      const s = rangeStart <= date ? rangeStart : date;
      const e = rangeStart <= date ? date : rangeStart;
      onRangeChange({ start: s, end: e });
      setHovered(null);
    }
  };

  const handleHover = date => {
    if (rangeStart && !rangeEnd) setHovered(date);
  };

  const shared = {
    start: rangeStart,
    end: rangeEnd,
    hovered: rangeStart && !rangeEnd ? hovered : null,
    onDayClick: handleClick,
    onDayHover: handleHover,
    minDate,
    maxDate,
  };

  const containerStyle = {
    display: "flex",
    flexDirection: "row",
    gap: 24,
    width: "100%",
  };
  const colStyle = { flex: 1, position: "relative", paddingTop: 32 };
  const navBtnStyle = {
    position: "absolute",
    top: 0,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#64748b",
    padding: "4px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
  };

  return (
    <div style={containerStyle} onMouseLeave={() => setHovered(null)}>
      <div style={colStyle}>
        <button
          style={{ ...navBtnStyle, left: 8 }}
          onClick={() => {
            const d = new Date(viewYear, viewMonth - 1, 1);
            setViewYear(d.getFullYear());
            setViewMonth(d.getMonth());
          }}
        >
          <ChevronLeft size={16} />
        </button>
        <MonthGrid {...shared} year={viewYear} month={viewMonth} />
      </div>
      <div style={{ width: 1, background: "#e2e8f0", alignSelf: "stretch" }} />
      <div style={colStyle}>
        <button
          style={{ ...navBtnStyle, right: 8 }}
          onClick={() => {
            const d = new Date(viewYear, viewMonth + 1, 1);
            setViewYear(d.getFullYear());
            setViewMonth(d.getMonth());
          }}
        >
          <ChevronRight size={16} />
        </button>
        <MonthGrid {...shared} year={m2.getFullYear()} month={m2.getMonth()} />
      </div>
    </div>
  );
}
